const HERO_SELECTOR = '.media-with-content[data-dynamic-image-color-match="true"]';
const SECTION_BACKGROUND_SELECTOR = '.section-background';
const SAMPLE_HEIGHT = 72;
const MIN_SAMPLE_WIDTH = 12;
const MAX_SAMPLE_WIDTH = 32;
const SAMPLE_HEIGHT_RATIO = 0.6;
const SAMPLE_TOP_OFFSET_RATIO = 0.08;

/**
 * @param {HTMLElement} section
 */
function clearDynamicBackground(section) {
  delete section.dataset.sampledColorRgb;
  updateBackgroundVariables(section, null);
}

/**
 * @param {HTMLElement} section
 * @param {{r: number, g: number, b: number} | null} rgb
 */
function updateBackgroundVariables(section, rgb) {
  const targets = [section];
  const sectionBackground = section.previousElementSibling;
  const contentSurface = section.querySelector('.media-with-content__content');
  const contentGroup = contentSurface?.closest('.group-block') || contentSurface;

  if (sectionBackground instanceof HTMLElement && sectionBackground.matches(SECTION_BACKGROUND_SELECTOR)) {
    targets.push(sectionBackground);
  }

  if (contentSurface instanceof HTMLElement) {
    targets.push(contentSurface);
  }

  if (contentGroup instanceof HTMLElement && !targets.includes(contentGroup)) {
    targets.push(contentGroup);
  }

  targets.forEach((target) => {
    if (rgb) {
      const backgroundValue = `rgb(${rgb.r} ${rgb.g} ${rgb.b})`;
      target.style.setProperty('--color-background', `rgb(${rgb.r} ${rgb.g} ${rgb.b})`);
      target.style.setProperty('--color-background-rgb', `${rgb.r} ${rgb.g} ${rgb.b}`);
      target.style.backgroundColor = backgroundValue;
      target.dataset.dynamicColorMatched = 'true';
    } else {
      target.style.removeProperty('--color-background');
      target.style.removeProperty('--color-background-rgb');
      target.style.removeProperty('background-color');
      delete target.dataset.dynamicColorMatched;
    }
  });
}

/**
 * @param {HTMLElement} section
 * @param {{r: number, g: number, b: number}} rgb
 */
function rememberSampledColor(section, rgb) {
  const serializedColor = `${rgb.r},${rgb.g},${rgb.b}`;
  section.dataset.sampledColorRgb = serializedColor;

  const cacheKey = section.dataset.dynamicImageCacheKey;

  if (!cacheKey) {
    return;
  }

  try {
    window.localStorage.setItem(cacheKey, serializedColor);
  } catch (_error) {}
}

/**
 * @param {HTMLElement} section
 * @returns {{r: number, g: number, b: number} | null}
 */
function getRememberedSampledColor(section) {
  const rememberedColor = section.dataset.sampledColorRgb;

  if (!rememberedColor) {
    return null;
  }

  const [r, g, b] = rememberedColor.split(',').map((value) => Number.parseInt(value, 10));

  if ([r, g, b].some((value) => Number.isNaN(value))) {
    return null;
  }

  return { r, g, b };
}

/**
 * @param {HTMLImageElement} image
 * @returns {{sourceX: number, sourceY: number, sourceWidth: number, sourceHeight: number} | null}
 */
function getVisibleSourceRect(image) {
  const naturalWidth = image.naturalWidth;
  const naturalHeight = image.naturalHeight;
  const renderedWidth = image.clientWidth;
  const renderedHeight = image.clientHeight;

  if (!naturalWidth || !naturalHeight || !renderedWidth || !renderedHeight) {
    return null;
  }

  const computedStyle = window.getComputedStyle(image);
  const objectFit = computedStyle.objectFit || 'fill';

  if (objectFit === 'contain' || objectFit === 'scale-down') {
    return {
      sourceX: 0,
      sourceY: 0,
      sourceWidth: naturalWidth,
      sourceHeight: naturalHeight,
    };
  }

  if (objectFit !== 'cover') {
    return {
      sourceX: 0,
      sourceY: 0,
      sourceWidth: naturalWidth,
      sourceHeight: naturalHeight,
    };
  }

  const scale = Math.max(renderedWidth / naturalWidth, renderedHeight / naturalHeight);
  const visibleWidth = renderedWidth / scale;
  const visibleHeight = renderedHeight / scale;
  const sourceX = (naturalWidth - visibleWidth) / 2;
  const sourceY = (naturalHeight - visibleHeight) / 2;

  return {
    sourceX,
    sourceY,
    sourceWidth: visibleWidth,
    sourceHeight: visibleHeight,
  };
}

/**
 * Sample the visible edge that meets the text panel, while biasing toward the
 * upper-middle portion to avoid food shadows near the bottom edge.
 *
 * @param {HTMLImageElement} image
 * @param {'left' | 'right'} edge
 * @returns {{r: number, g: number, b: number} | null}
 */
function sampleEdgeColor(image, edge) {
  const sourceRect = getVisibleSourceRect(image);

  if (!sourceRect) {
    return null;
  }

  const sourceWidth = Math.max(1, Math.round(sourceRect.sourceWidth));
  const sourceHeight = Math.max(1, Math.round(sourceRect.sourceHeight));
  const sampleWidth = Math.min(MAX_SAMPLE_WIDTH, Math.max(MIN_SAMPLE_WIDTH, Math.round(sourceWidth * 0.08)));
  const sampleHeight = Math.max(1, Math.round(sourceHeight * SAMPLE_HEIGHT_RATIO));
  const sampleTopOffset = Math.max(0, Math.round(sourceHeight * SAMPLE_TOP_OFFSET_RATIO));
  const sourceX =
    edge === 'left'
      ? Math.round(sourceRect.sourceX)
      : Math.round(sourceRect.sourceX + sourceWidth - sampleWidth);
  const sourceY = Math.min(
    Math.round(sourceRect.sourceY + sampleTopOffset),
    Math.max(0, Math.round(sourceRect.sourceY + sourceHeight - sampleHeight)),
  );

  const canvas = document.createElement('canvas');
  canvas.width = sampleWidth;
  canvas.height = SAMPLE_HEIGHT;

  const context = canvas.getContext('2d', { willReadFrequently: true });
  if (!context) {
    return null;
  }

  try {
    context.drawImage(
      image,
      sourceX,
      sourceY,
      sampleWidth,
      sampleHeight,
      0,
      0,
      sampleWidth,
      SAMPLE_HEIGHT,
    );
  } catch (_error) {
    return null;
  }

  let imageData;

  try {
    imageData = context.getImageData(0, 0, sampleWidth, SAMPLE_HEIGHT).data;
  } catch (_error) {
    return null;
  }

  let red = 0;
  let green = 0;
  let blue = 0;
  let visiblePixelCount = 0;

  for (let index = 0; index < imageData.length; index += 4) {
    const alpha = imageData[index + 3];

    if (alpha < 16) {
      continue;
    }

    red += imageData[index];
    green += imageData[index + 1];
    blue += imageData[index + 2];
    visiblePixelCount += 1;
  }

  if (!visiblePixelCount) {
    return null;
  }

  return {
    r: Math.round(red / visiblePixelCount),
    g: Math.round(green / visiblePixelCount),
    b: Math.round(blue / visiblePixelCount),
  };
}

/**
 * @param {HTMLElement} section
 * @returns {HTMLImageElement | null}
 */
function getSectionImage(section) {
  if (section.dataset.mediaType !== 'image') {
    return null;
  }

  const image = section.querySelector('.media-block img');

  return image instanceof HTMLImageElement ? image : null;
}

/**
 * @param {HTMLElement} section
 */
function applySampledBackground(section) {
  const image = getSectionImage(section);

  if (!image) {
    clearDynamicBackground(section);
    return;
  }

  if (!image.complete || !image.naturalWidth || !image.naturalHeight) {
    return;
  }

  const edge = section.dataset.mediaPosition === 'left' ? 'right' : 'left';
  const sampledColor = sampleEdgeColor(image, edge);

  if (!sampledColor) {
    const rememberedColor = getRememberedSampledColor(section);

    if (rememberedColor) {
      updateBackgroundVariables(section, rememberedColor);
    }

    return;
  }

  rememberSampledColor(section, sampledColor);
  updateBackgroundVariables(section, sampledColor);
}

/**
 * @param {HTMLElement} section
 */
function bindSection(section) {
  if (section.dataset.dynamicImageColorBound === 'true') {
    applySampledBackground(section);
    return;
  }

  section.dataset.dynamicImageColorBound = 'true';

  const image = getSectionImage(section);

  if (!image) {
    clearDynamicBackground(section);
    return;
  }

  let lastCurrentSrc = image.currentSrc;

  const resample = () => {
    if (image.currentSrc && image.currentSrc !== lastCurrentSrc) {
      lastCurrentSrc = image.currentSrc;
    }

    applySampledBackground(section);
  };

  if (image.complete) {
    resample();
  } else {
    image.addEventListener('load', resample, { once: true });
  }

  image.addEventListener('load', resample);

  if ('ResizeObserver' in window) {
    const resizeObserver = new ResizeObserver(() => {
      window.requestAnimationFrame(resample);
    });

    resizeObserver.observe(image);
  }
}

function initializeDynamicHeroColorMatching(root = document) {
  if (root instanceof HTMLElement && root.matches(HERO_SELECTOR)) {
    bindSection(root);
  }

  root.querySelectorAll(HERO_SELECTOR).forEach((section) => {
    if (section instanceof HTMLElement) {
      bindSection(section);
    }
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => initializeDynamicHeroColorMatching());
} else {
  initializeDynamicHeroColorMatching();
}

document.addEventListener('shopify:section:load', (event) => {
  if (event.target instanceof HTMLElement) {
    initializeDynamicHeroColorMatching(event.target);
  }
});
